import React, { useState, useEffect, useMemo } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  AiOutlineUserAdd,
  AiOutlineSearch,
  AiOutlineExclamationCircle
} from "react-icons/ai";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";

import FilterService from "../../services/FilterService";
import AddFilterModal from "../../modals/AddFilter/AddFilter";
import EditFilterModal from "../../modals/EditFilter/EditFilter";
import PrintableTable from "../../components/PrintableTable/PrintableTable";
import Loader from "../../components/Loader/Loader";

import "./FiltersList.css";
import "../../styles/action-icons.css";

const FiltersList = () => {
  const [filters, setFilters] = useState([]);
  const [filteredFilters, setFilteredFilters] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilterIds, setSelectedFilterIds] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedFilterId, setSelectedFilterId] = useState(0);
  const [selectedFilter, setSelectedFilter] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });

  // Load filters
  const loadFilters = async () => {
    setLoading(true);
    try {
      const res = await FilterService.getAll();
      const list = res.data.filters || res.data || [];
      setFilters(list);
      setFilteredFilters(list);
    } catch (err) {
      console.error("Failed to fetch filters:", err);
      toast.error("Failed to load filters");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFilters();
  }, []);

  // Filter logic
  useEffect(() => {
    const lower = searchTerm.toLowerCase();
    setFilteredFilters(
      filters.filter(f =>
        String(f.ID).includes(lower) ||
        (f["Filter Name"] || "").toLowerCase().includes(lower) ||
        (f["Filter Code"] || "").toLowerCase().includes(lower) ||
        String(f["Lasting Duration (Months)"]).includes(lower)
      )
    );
  }, [searchTerm, filters]);

  // Handlers
  const editClickHandler = (e, filter) => {
    e.stopPropagation();
    setSelectedFilter(filter || {});
    setShowEditModal(true);
  };

  const deleteClickHandler = (e, id) => {
    e.stopPropagation();
    setSelectedFilterId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await FilterService.remove(selectedFilterId);
      toast.success("Filter deleted successfully", { autoClose: 2000 });
      await loadFilters();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete filter");
    } finally {
      setShowDeleteModal(false);
      setSelectedFilterId(0);
    }
  };

  // Checkbox handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedFilterIds(filteredFilters.map(f => f.ID));
    } else {
      setSelectedFilterIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedFilterIds(prev =>
      prev.includes(id) ? prev.filter(fid => fid !== id) : [...prev, id]
    );
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    if (selectedFilterIds.length === 0) return;
    if (!window.confirm("Are you sure you want to delete the selected filters?")) return;
    try {
      await Promise.all(selectedFilterIds.map(id => FilterService.remove(id)));
      toast.success("Selected filters deleted successfully", { autoClose: 2000 });
      setSelectedFilterIds([]);
      await loadFilters();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete selected filters");
    }
  };

  const getComparableValue = (filter, key) => {
    switch (key) {
      case 'id':
        return Number(filter.ID) || 0;
      case 'name':
        return (filter["Filter Name"] || "").toLowerCase();
      case 'code':
        return (filter["Filter Code"] || "").toLowerCase();
      case 'duration':
        return Number(filter["Lasting Duration (Months)"]) || 0;
      default:
        return '';
    }
  };

  const sortedFilters = useMemo(() => {
    const data = [...filteredFilters];
    if (!sortConfig?.key) return data;
    data.sort((a, b) => {
      const aVal = getComparableValue(a, sortConfig.key);
      const bVal = getComparableValue(b, sortConfig.key);
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return data;
  }, [filteredFilters, sortConfig]);

  const filtersToDisplay = selectedFilterIds.length > 0
    ? sortedFilters.filter(f => selectedFilterIds.includes(f.ID))
    : sortedFilters;

  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="sort-icon" aria-hidden="true" />;
    return sortConfig.direction === 'asc'
      ? <FaSortUp className="sort-icon" aria-hidden="true" />
      : <FaSortDown className="sort-icon" aria-hidden="true" />;
  };

  if (loading) {
    return <Loader message="Loading filters..." />;
  }

  return (
    <>
      <div className="filters-list">
        <div className="filters-title">Filters</div>

        <div className="filters-header row">
          <div className="col add-btn-col">
            <button
              className="btn add-filter-button"
              onClick={() => setShowAddModal(true)}
            >
              <AiOutlineUserAdd /> Add Filter
            </button>
          </div>
          <div className="col search-bar-col">
            <div className="search-bar-wrapper">
              <span className="search-icon">
                <AiOutlineSearch />
              </span>
              <input
                type="text"
                placeholder="Search Filters..."
                className="search-bar"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Bulk action buttons */}
        {selectedFilterIds.length > 0 && (
          <div className="bulk-actions">
            <button className="btn btn-danger" onClick={handleBulkDelete}>
              <FiTrash2 /> Delete Selected
            </button>
          </div>
        )}

        <div className="table-responsive">
          <PrintableTable
            fileName="filters.pdf"
            title="Filters List"
            showOptions={true}
            data={filtersToDisplay}
          >
            <table className="table filters-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={selectedFilterIds.length === filteredFilters.length && filteredFilters.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>
                    <button type="button" className="sortable-header" onClick={() => handleSort('id')}>
                      ID {renderSortIcon('id')}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="sortable-header" onClick={() => handleSort('name')}>
                      Filter Name {renderSortIcon('name')}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="sortable-header" onClick={() => handleSort('code')}>
                      Filter Code {renderSortIcon('code')}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="sortable-header" onClick={() => handleSort('duration')}>
                      Lasting Duration (Months) {renderSortIcon('duration')}
                    </button>
                  </th>
                  <th className="no-print">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtersToDisplay.length > 0 ? (
                  filtersToDisplay.map(filter => {
                    const id = filter.ID;
                    const name = filter["Filter Name"] || "";
                    const code = filter["Filter Code"] || "";
                    const duration = filter["Lasting Duration (Months)"] || "";
                    return (
                      <tr key={id} style={{ cursor: "default" }}>
                        <td className="text-center">
                          <input
                            type="checkbox"
                            checked={selectedFilterIds.includes(id)}
                            onChange={() => handleSelectOne(id)}
                          />
                        </td>
                        <td className="text-center">{id}</td>
                        <td className="text-center">{name}</td>
                        <td className="text-center">{code}</td>
                        <td className="text-center">{duration}</td>
                        <td className="text-center no-print">
                          <div className="table-action-group">
                            <button
                              type="button"
                              className="table-icon-button edit-button no-click"
                              onClick={e => editClickHandler(e, filter)}
                              title="Edit filter"
                            >
                              <FiEdit2 />
                            </button>
                            <button
                              type="button"
                              className="table-icon-button delete-button no-click"
                              onClick={e => deleteClickHandler(e, id)}
                              title="Delete filter"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="6">No filters found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </PrintableTable>
        </div>

        <ToastContainer />

        <AddFilterModal
          showModal={showAddModal}
          handleClose={() => setShowAddModal(false)}
          onFilterAdded={loadFilters}
        />

        <EditFilterModal
          showModal={showEditModal}
          handleClose={() => {
            setShowEditModal(false);
            setSelectedFilter({});
          }}
          filterData={selectedFilter || {}}
          onFilterUpdated={loadFilters}
        />

        <Modal
          show={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          className="delete-confirmation-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title className="title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-danger)' }}>
              <AiOutlineExclamationCircle style={{ fontSize: '1.6em', color: 'var(--color-danger)' }} />
              Confirm Deletion
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="confirmation-text" style={{ fontSize: 'var(--font-size-md)', color: 'var(--color-text)', margin: '1rem 0', textAlign: 'center' }}>
              Are you sure you want to delete this filter? This action cannot be undone.
            </div>
          </Modal.Body>
          <Modal.Footer style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
            <Button
              variant="secondary"
              onClick={() => setShowDeleteModal(false)}
              className="w-100"
              style={{ maxWidth: 120, background: 'var(--color-muted)', color: 'var(--color-text-light)', border: 'none' }}
            >
              No
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirmDelete}
              className="w-100"
              style={{ maxWidth: 120, background: 'var(--color-danger)', color: 'var(--color-text-light)', border: 'none' }}
            >
              Yes
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export default FiltersList;
